import type { CustomOrderAsset } from '../api/customOrdersApi';

/**
 * Renders a custom-order asset for admin review using the short-lived signed read
 * url minted by the BFF. Videos still transcoding show a processing state instead
 * of a broken player; assets whose url could not be signed show a fallback.
 */
export const AssetReview = ({ asset }: { asset: CustomOrderAsset }) => {
  const processing = asset.transcodeState === 'processing';
  const failed = asset.transcodeState === 'failed';

  return (
    <div className="rounded-[12px] border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-50">
        <span className="text-[11px] font-[600] uppercase tracking-wide text-muted-foreground">
          {asset.assetType}
        </span>
        {processing && (
          <span className="text-[11px] font-[600] px-2 py-0.5 rounded-full bg-[#FEFECD] text-[#7A6B00]">
            Processing…
          </span>
        )}
        {failed && (
          <span className="text-[11px] font-[600] px-2 py-0.5 rounded-full bg-[#FCE2E2] text-[#A63F3F]">
            Transcode failed
          </span>
        )}
      </div>

      <div className="p-3">
        {processing ? (
          <div className="flex h-40 items-center justify-center text-[13px] text-muted-foreground">
            This video is still being processed. Check back shortly.
          </div>
        ) : !asset.readUrl ? (
          <div className="flex h-40 items-center justify-center text-[13px] text-muted-foreground">
            Preview unavailable.
          </div>
        ) : asset.assetType === 'image' ? (
          <a href={asset.readUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={asset.readUrl}
              alt="Custom order asset"
              className="max-h-64 w-full rounded-[8px] object-contain bg-muted"
            />
          </a>
        ) : asset.assetType === 'video' ? (
          <video src={asset.readUrl} controls className="max-h-64 w-full rounded-[8px] bg-black" />
        ) : asset.assetType === 'audio' ? (
          <audio src={asset.readUrl} controls className="w-full" />
        ) : (
          <a
            href={asset.readUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13px] text-blue-600 hover:underline"
          >
            Download attachment
          </a>
        )}
      </div>
    </div>
  );
};
